import { useCallback, useState } from "react"
import type { MutableRefObject } from "react"
import type { QueryClient } from "@tanstack/react-query"
import type { AxiosResponse } from "axios"
import {
	insertFurnitureIntoCache,
	reorderFurnitureInCache,
	spliceErefIntoCache,
} from "helpers/quotationCache.helper"
import { useCreateFurniture, useUpdateFurniture, useUpdateFurnitureFixedPrice } from "hooks/api/quotation/quotations.hook"
import AnnotationService from "services/annotations.service"
import type {
	TCreateFurnitureResponse,
	TFurniture,
	TFurnitureCustomization,
	TFurnitureFixedPriceConfig,
	TUpdateFurnitureResponse,
} from "types/elevationReferences.type"
import type { TQuotationDetail } from "types/quotations.type"
import type { TPaneBinding } from "../core/viewerPanes"
import type { TViewerTool } from "../core/viewerTools"
import { useAreaCreate, type TAreaCreateSession } from "./useAreaCreate"
import { useApplyCustomization } from "./useApplyCustomization"

/** Live "add other object" session — the shared area-create session under its own name. */
export type TOtherCreateSession = TAreaCreateSession

/** What the create panel has collected by the time Create object is pressed. */
export type TOtherCreateDetails = {
	width: number
	height: number
	depth: number
	/** "Other" name. Lives on the fixed-price config, so it is PATCHed after the create. */
	name: string
	/** "Other" fixed price, same story as `name`. */
	price: number
	/** The elevation reference to link to, at the version the cache last saw. Optional (REQ-299). */
	eref: { id: number; version: number } | null
	/**
	 * REQ-413/414. Null/absent unless the panel's Customization section is expanded AND complete —
	 * the customization endpoint is per-object, so it becomes a fourth link in the chain below.
	 */
	customization?: TFurnitureCustomization | null
}

type Props = {
	/** Orchestrator-owned mirror of the live session (see useAreaCreate). */
	createSessionRef: MutableRefObject<TOtherCreateSession | null>
	/** Plain (non-React) draft box + its four corner handles, from createOverlayContainers. */
	draftRectRef: MutableRefObject<{ element: HTMLElement; handles: HTMLElement[] } | null>
	/** `.App` element — the origin the box's app-relative coordinates are measured against. */
	appContainerRef: MutableRefObject<HTMLElement | null>
	/** The live viewer panes, re-resolved on every call (they are replaced on a layout change). */
	getViewerPanes: () => TPaneBinding[]
	/** Bumped by split-screen enter/exit; re-binds the canvas listeners onto the new panes. */
	layoutEpoch: number
	numericQuotationIdRef: MutableRefObject<number | undefined>
	queryClient: QueryClient
	/** Post-create hand-off to the orchestrator: select the new object and open its sidebar. */
	onFurnitureCreated: (furniture: TFurniture) => void
	/** The shared tool command (useCanvasTools) — the mode puts every pane back into select. */
	onToolChange: (tool: TViewerTool) => void
	clearSelectionToolbar: () => void
	success: (text: string) => void
	formatError: (error: unknown) => void
}

/**
 * "Add other object" mode (REQ-299), opened from the header's + Add → Other menu. It is the
 * countertop flow's sibling: the same shared `useAreaCreate` gesture defines the object's area, and
 * this hook owns the create request — `POST /quotations/{id}/furniture/` with the area plus the
 * details the panel collected.
 *
 * An "Other" object is the one with NO category at any level (see `isOtherFurniture`), which is
 * exactly what `related_category_id: null` produces: the backend answers a null category by giving
 * the new furniture the empty fixed-price config that puts it in "Other" mode.
 *
 * That config is also why creation is a CHAIN rather than one request. The create endpoint takes
 * neither `name` nor `fixed_price` — both live on the config it has only just created — so the two
 * required fields of the panel are PATCHed in right after, each against the version the previous
 * step returned:
 *
 *   1. `POST   /quotations/{id}/furniture/`                    → the object, in Other mode
 *   2. `PATCH  /quotations/{id}/furniture/{id}/`      `name`   → rejected outside Other mode
 *   3. `PATCH  /quotations/{id}/furniture/{id}/fixed-price/`   → same
 *   4. `PUT    /quotations/{id}/furniture/{id}/customization/` → only if the panel filled one in
 *
 * Steps 2, 3 and 4 are best-effort ON PURPOSE. Once step 1 succeeds the object EXISTS, and dropping
 * the flow there would leave it invisible until the next refetch; so a failure in either PATCH is
 * surfaced as a toast and the hand-off still happens, landing the user in that object's own sidebar
 * with the same two fields ready to be retyped. Only step 1 failing keeps the mode open.
 *
 * The response is applied straight to the cache and the canvas, exactly as the cabinet flow does
 * (useFurnitureCreate): insert, then apply the backend-decided group order, then splice the eref
 * back at its bumped version. `insertFurnitureIntoCache` no-ops when no cached page matches the new
 * object's page, and drawing the highlight anyway would leave a canvas-only object that vanishes on
 * the next full re-render — so that case refetches instead, and the hand-off still runs (#1310
 * review: returning early there left the right panel blank in a mode whose session was gone).
 */
export const useOtherCreate = ({
	createSessionRef,
	draftRectRef,
	appContainerRef,
	getViewerPanes,
	layoutEpoch,
	numericQuotationIdRef,
	queryClient,
	onFurnitureCreated,
	onToolChange,
	clearSelectionToolbar,
	success,
	formatError,
}: Props) => {
	const { mutateAsync: createFurnitureAsync } = useCreateFurniture()
	const { mutateAsync: updateFurnitureAsync } = useUpdateFurniture()
	const { mutateAsync: updateFurnitureFixedPriceAsync } = useUpdateFurnitureFixedPrice()
	const applyCustomization = useApplyCustomization(formatError)
	const [isCreatePending, setIsCreatePending] = useState(false)

	const areaCreate = useAreaCreate({
		createSessionRef,
		draftRectRef,
		appContainerRef,
		getViewerPanes,
		layoutEpoch,
		onToolChange,
		clearSelectionToolbar,
	})
	const { cancelCreateArea } = areaCreate

	// Steps 2 + 3 of the chain. Returns the object as the backend last described it, so the caller
	// caches the name and price it just set rather than the empty config the create came back with.
	// Throws nothing: a rejected PATCH is reported and the best-known object is returned.
	//
	// `isVersionSuspect` says whether the version travelling with that object is still one the
	// backend would accept. Once a step has been rejected it is not, and every later step in the
	// chain — the customization PUT included — would only produce a second, identical error.
	const applyOtherFields = useCallback(
		async (
			quotationId: number,
			created: TFurniture,
			details: TOtherCreateDetails,
		): Promise<{ furniture: TFurniture; isVersionSuspect: boolean }> => {
			let furniture = created
			const name = details.name.trim()

			try {
				const response = await updateFurnitureAsync({
					quotationId,
					furnitureId: furniture.id,
					body: { version: furniture.version, name },
				})
				const updated = (response?.data as TUpdateFurnitureResponse | undefined)?.this_furniture
				if (updated) furniture = updated
			} catch (error) {
				// The version we hold is suspect from here, so pricing it would only produce a second,
				// identical error.
				formatError(error)
				return { furniture, isVersionSuspect: true }
			}

			try {
				const response = await updateFurnitureFixedPriceAsync({
					quotationId,
					furnitureId: furniture.id,
					body: { version: furniture.version, fixed_price: details.price },
				})
				// This endpoint answers with the furniture's version + its config only, not the whole
				// object — so the two fields are merged onto what we already have.
				const data = response?.data as
					| { version?: number; fixed_price_config?: TFurnitureFixedPriceConfig | null }
					| undefined
				furniture = {
					...furniture,
					version: typeof data?.version === "number" ? data.version : furniture.version + 1,
					fixed_price_config: data?.fixed_price_config ?? {
						fixed_price: details.price,
						name: furniture.fixed_price_config?.name ?? name,
					},
				}
			} catch (error) {
				formatError(error)
				return { furniture, isVersionSuspect: true }
			}

			return { furniture, isVersionSuspect: false }
		},
		[updateFurnitureAsync, updateFurnitureFixedPriceAsync, formatError],
	)

	const runCreateOther = useCallback(
		async (details: TOtherCreateDetails) => {
			const quotationId = numericQuotationIdRef.current
			const area = createSessionRef.current?.area
			if (quotationId == null || Number.isNaN(quotationId) || !area) return
			setIsCreatePending(true)
			try {
				const response = await createFurnitureAsync({
					quotationId,
					body: {
						page_number: area.pageNumber,
						x0: area.coordinates.x0,
						y0: area.coordinates.y0,
						x1: area.coordinates.x1,
						y1: area.coordinates.y1,
						// Null is what makes the new object an "Other" one — it is not a missing value.
						related_category_id: null,
						width: details.width,
						height: details.height,
						depth: details.depth,
						// Sent as a pair or not at all — the endpoint rejects a version without an id and
						// uses the version for the eref's optimistic lock.
						...(details.eref ? { eref_id: details.eref.id, eref_version: details.eref.version } : {}),
					},
				})
				const data = response?.data as TCreateFurnitureResponse | undefined
				const createdFurniture = data?.this_furniture
				if (!createdFurniture) return

				// Steps 2 + 3, then the customization's own PUT (REQ-413/414) — one more call, and only
				// when the panel's section actually holds one. Past this line the object exists no matter
				// how any of them went, so every path below tears the mode down and hands over.
				const { furniture: withOtherFields, isVersionSuspect } = await applyOtherFields(
					quotationId,
					createdFurniture,
					details,
				)
				// Skipped when an earlier step was rejected: its version is no longer one the backend
				// would accept, so the PUT could only fail the same way and toast a second time.
				const created = isVersionSuspect
					? withOtherFields
					: await applyCustomization(quotationId, withOtherFields, details.customization ?? null)

				let isInCache = false
				queryClient.setQueryData<AxiosResponse<TQuotationDetail> | undefined>(["quotation", quotationId], (cached) => {
					// Insert first (the ordering references the new id), then apply the backend-decided
					// group order so the object lands in the sidebar where the backend put it — not just
					// appended last. Splice the eref last so its bumped version is reflected too.
					let next = insertFurnitureIntoCache(cached, created)
					if (next !== cached) isInCache = true
					if (data?.ordering) next = reorderFurnitureInCache(next, data.ordering)
					return data?.updated_eref ? spliceErefIntoCache(next, data.updated_eref) : next
				})

				// No cached page to hold the object, so the splice above was a no-op: refetch instead of
				// drawing a canvas-only highlight that the next full re-render would drop.
				if (!isInCache) queryClient.invalidateQueries({ queryKey: ["quotation", quotationId] })

				// Tear the mode down BEFORE handing over: the hand-off opens the new object's own
				// sidebar, which would otherwise be fighting the create panel for the right panel.
				cancelCreateArea()

				// The hand-off runs on BOTH paths — it is the only thing that moves the right panel off
				// "create-other", and skipping it on the refetch path leaves the panel in a mode whose
				// session is gone, i.e. blank with no way back (#1310 review).
				if (isInCache) AnnotationService.addFurnitureAnnotation(created)
				onFurnitureCreated(created)
				if (isInCache) AnnotationService.selectFurniture(created.id)
				success("Object created")
			} catch (error) {
				// The area and the panel stay exactly as they were so the user can fix whatever the
				// backend rejected (duplicate coordinates, a stale eref version) and retry.
				formatError(error)
			} finally {
				setIsCreatePending(false)
			}
		},
		[
			numericQuotationIdRef,
			createSessionRef,
			createFurnitureAsync,
			applyOtherFields,
			applyCustomization,
			queryClient,
			cancelCreateArea,
			onFurnitureCreated,
			success,
			formatError,
		],
	)

	return {
		createSession: areaCreate.createSession,
		isCreatePending,
		startCreateOther: areaCreate.startCreateArea,
		cancelCreateOther: areaCreate.cancelCreateArea,
		runCreateOther,
	}
}
